/**
 * The artist / estate portal, and the words for every screen of its own.
 *
 * An estate is the party the registry has appointed to speak for an artist's
 * body of work, and its rail runs from the catalogue raisonné through the
 * studio book, the loan and consign forms and the transaction centre. This
 * file is the portal spec the shell mounts plus the copy of the four screens
 * that live in this folder; each has its own model beside it —
 * `catalogue.model.ts`, `studio.model.ts`, `registerStudio.model.ts`,
 * `loansConsign.model.ts` — and the contacts and settlement screens are
 * remounted from the gallery's and the collector's folders.
 *
 * Every copy block here is bounded by a migration decision about what the
 * estate may see: the catalogue says nothing about where a work sits or
 * whether it sold, and the studio's lifecycle is derived rather than stored.
 */

import type { LoanStatus } from "@ondo/shared";

import { definePortal } from "../shell/portal.ts";

/**
 * Where inside the artist / estate portal the reader is.
 *
 * The fifth and last portal, and the only one whose front page is a list of
 * works the workspace neither owns nor holds. A collector's collection is
 * theirs, a gallery's inventory is stock it was handed to sell, an agent's
 * mandates reach a work and stop. An estate's catalogue is none of those: it is
 * every work anybody has registered as being BY its artist, wherever it now
 * sits and whoever now owns it.
 *
 * That relation is `ondo.works.attributed_principal_id`, and `works_read` has
 * admitted it since 20260803110000 — `docs/accessFGARules.md` describes the
 * branch as "intended for an estate maintaining a catalogue raisonné". The read
 * has been waiting for a portal for longer than any other in this product.
 */
export type EstateDestination =
  | "catalogue"
  | "studio"
  | "contacts"
  | "loans"
  | "transactions"
  | "authentications"
  | "verifications"
  | "notifications"
  | "notification-preferences"
  | "about"
  | "activity"
  | "profile"
  | "work";

/**
 * The estate workspace's own copy.
 *
 * Every heading here says STUDIO or ESTATE rather than COLLECTION, and the
 * difference is the same load-bearing one the gallery's INVENTORY makes. A
 * collection is a holding. A catalogue is a scholarly claim about a body of
 * work, made by the party the registry has appointed to make it — and most of
 * what it lists belongs to somebody else.
 */
export const ESTATE_COPY = {
  eyebrow: "Artist / Estate",
  navLabel: "Artist / Estate workspace",
} as const;

export const ESTATE_PORTAL = definePortal<EstateDestination>({
  eyebrow: ESTATE_COPY.eyebrow,
  navLabel: ESTATE_COPY.navLabel,

  destinations: [
    { id: "catalogue", label: "Artworks / Catalogue Raisonné", glyph: "◻", state: "ready", group: "main" },
    { id: "studio", label: "Studio / Production", glyph: "⚒", state: "ready", group: "main" },
    // OND-61, the same gate the gallery rail carries and for the same reason:
    // this screen is the same `ondo.my_contacts()` and the ticket's cast is a
    // STUDIO's six seats, so the estate portal is where the Scholar and the
    // Authentication Specialist actually sit.
    {
      id: "contacts",
      label: "Contacts & CRM",
      glyph: "◉",
      state: "ready",
      group: "main",
      requires: "collector_list_read",
    },
    { id: "loans", label: "Loans & Consign", glyph: "⇄", state: "ready", group: "main" },
    { id: "transactions", label: "Transaction Centre", glyph: "→", state: "ready", group: "main" },
    // READY SINCE OND-212. It was `planned` from the day the portal shipped,
    // and for a while truthfully: nothing could name an estate as the party to
    // confirm who made a work. OND-20 built that ask and OND-60 the service
    // around it; this screen is the two pulled out of the general inbox and
    // laid beside the works the estate has confirmed.
    { id: "authentications", label: "Authentication Requests", glyph: "✦", state: "ready", group: "main" },
    { id: "verifications", label: "Verifications", glyph: "✓", state: "ready", group: "main" },
    { id: "notifications", label: "Notifications", glyph: "⊘", state: "ready", group: "foot" },
    { id: "notification-preferences", label: "Notification Settings", glyph: "⚙", state: "ready", group: "foot" },
    { id: "about", label: "About ONDO", glyph: "i", state: "ready", group: "foot" },
    { id: "activity", label: "Activity & access log", glyph: "≡", state: "ready", group: "foot" },
    // Listed as well as reachable from the identity card, for the reason every
    // other rail gives: a control whose only affordance is that it happens to be
    // clickable is one most people never press.
    { id: "profile", label: "My Profile", glyph: "◐", state: "ready", group: "foot" },
    { id: "work", label: "Artwork record", glyph: "◈", state: "unlisted", group: "main" },
  ],

  headers: {
    catalogue: {
      title: "Artworks / Catalogue Raisonné",
      // Names the subject because a catalogue belongs to an artist, not to the
      // reader — a studio director reads a screen about the ESTATE's artist, and
      // a subtitle saying "your works" would be wrong about whose they are. The
      // agent's rail makes the same move for the same reason.
      subtitle: (subject) => `${subject.name} — every work registered as being by this artist`,
    },
    studio: {
      title: "Studio / Production",
      subtitle: "Private production records — not visible to collectors",
    },
    contacts: {
      title: "Contacts & CRM",
      subtitle: "Collectors, galleries and institutions — transacted with, or added by you",
    },
    loans: {
      title: "Loans & Consignments",
      subtitle: (subject) =>
        `${subject.name} — loan works to a museum, or consign to a gallery or agent`,
    },
    transactions: {
      title: "Transaction Centre",
      subtitle: (subject) => `Revenue and royalties for ${subject.name}`,
    },
    authentications: {
      title: "Authentication Requests",
      subtitle: (subject) => `Works others have asked ${subject.name} to put its name to`,
    },
    verifications: {
      title: "Verifications",
      subtitle: (subject) =>
        `${subject.name} — confirm or dispute ONDO information others have asked you to verify`,
    },
    // Addressed to the ESTATE, for the reason the gallery's is addressed to the
    // firm: a consignment offered to the studio is told to the studio, and no
    // director's own feed carries it.
    notifications: {
      title: "Notifications",
      subtitle: (subject) => `${subject.name} — what has been sent here, and what needs an answer`,
    },
    "notification-preferences": {
      title: "Notification Settings",
      subtitle:
        "How each kind of notification reaches you — immediately, in the daily digest, or not at all",
    },
    about: { title: "About ONDO", subtitle: "" },
    activity: {
      title: "Activity & access log",
      // ANSWERED BY OND-68, and this comment is kept rather than deleted
      // because it names the condition that was met. It read: "The design draws
      // this screen as the STUDIO's — every seat, what each viewed and exported.
      // That screen cannot be built on the activity feed at all:
      // `ActivityFeedService.record()` swallows its errors, so a line can be
      // silently missing, and CLAUDE.md is explicit that a screen reading a feed
      // to answer 'what is true' is a bug. An audit trail is exactly such a
      // screen. It needs a durable table of its own before this subtitle can
      // change."
      //
      // It has one: `ondo.access_events`, append-only, undeletable by anybody
      // including the service role, and written INSIDE the request that serves
      // the read rather than emitted after it. So the subtitle changes — and
      // it changes to a hedged sentence rather than the design's flat claim,
      // because the firm-wide half is real only for an ADMINISTRATOR.
      // `access_events_read` gives an ordinary seat their own rows, so a
      // subtitle promising every seat's reads to a viewer would be the same
      // kind of over-claim one rung down.
      //
      // The FEED half of this screen is unchanged and still the caller's own
      // trail: `GET /v1/activity` takes a collection, a work or `scope=me` and
      // refuses to resolve a supplied id, so there is still no org-scoped feed
      // to read. That was never the half that needed fixing.
      subtitle:
        "Every act you have recorded, and who has opened a record — the firm's whole trail if you administer it, your own if not",
    },
    profile: {
      title: "My Profile",
      // Replaced at render on the estate's own workspace, where the subject is
      // the organisation rather than the reader — see PROFILE_HEADERS and
      // `profileAt`.
      subtitle: "Account, identity verification and membership",
    },
    work: {
      title: "Artwork record",
      subtitle: "What the registry holds for this work, and its chain of title",
    },
  },

  /**
   * NOTHING IS `planned` ON THIS RAIL ANY MORE.
   *
   * SIX ENTRIES LEFT THIS MAP when their relations landed, each rewritten
   * into the screen that replaced it rather than left to go stale: the
   * catalogue and its numbers (20260911090000), the studio record and the
   * loan ledger (20260911091000), the contacts read remounted, the artist's
   * settlement ledger remounted as the Transaction Centre — and last, the
   * authentication queue (OND-212). That one outlived the thing it waited on
   * by a week: OND-20 built the ask and OND-60 the service, both landed in the
   * Verifications inbox, and the placeholder here kept saying "This screen is
   * where it will get a queue of its own" with no ticket naming the screen.
   * `e2e/role-doors.spec.ts` pinned that placeholder and now pins the screen.
   *
   * The map stays, empty, because `definePortal` checks it and a rail item
   * declared `planned` with no entry here would render a blank — the failure
   * that spec exists to catch.
   */
  notYet: {},

  /**
   * The catalogue: the workspace's whole subject, and now a real screen.
   *
   * It spent one release pointing at the verification inbox, with a comment
   * promising it would move "the day attribution is restored". 20260911090000
   * is that day.
   */
  defaultDestination: "catalogue",

  records: {
    catalogue: "none",
    studio: "none",
    contacts: "none",
    loans: "none",
    transactions: "none",
    authentications: "none",
    verifications: "none",
    notifications: "none",
    "notification-preferences": "none",
    about: "none",
    activity: "none",
    profile: "none",
    work: "required",
  },

  /**
   * Where a work record with no work goes.
   *
   * The catalogue, because it is the only place an estate could honestly
   * choose a work FROM. An estate holds no collection by virtue of being one,
   * and its own studio inventory is a different list than the body of work it
   * is the authority on.
   */
  recordFallback: "catalogue",

  badges: { verifications: "verifications", notifications: "notifications" },

  profileAt: "profile",

  // No greeting. This portal's front page is a list of things other people need
  // from this estate, and "Welcome back" over an obligation is the wrong
  // register — the verifier and agent rails decline one for the same reason.
  greetsAt: null,

  /**
   * No HEADER action, and the design's "+ Register new work" is not missing:
   * it lives on the Studio page itself, top right of the list, as drawn.
   *
   * Every other portal's header button is a link to a full-page wizard. This
   * one opens a dialog over the list it adds to, and a header link cannot do
   * that — so the control sits where the list is, gated there on the admin
   * rung that `ondo.register_studio_work()` requires (20260912090000
   * decision 1). It was withheld until that function existed, on the
   * principle the verifier portal states: a control that refused every time
   * would be worse than its absence.
   */
  primaryAction: () => null,
  secondaryAction: () => null,
});

/**
 * The catalogue raisonné's own copy.
 *
 * Every sentence here is bounded by 20260911090000 decision 4: the screen may
 * say what a work IS and what this estate has put its name to, and nothing
 * about where any work sits, who holds it, or whether it has sold. The
 * design's Active / Never Sold / Museum colours are absent for that reason,
 * not forgotten — deriving them for works the estate does not own would
 * disclose transfers the policy withholds from a stranger to the sale.
 */
export const CATALOGUE_COPY = {
  loading: "Assembling the catalogue…",
  errorTitle: "Can’t load the catalogue",

  /** The column heading over the owner's name — OND-58. */
  ownerLabel: "Owner",
  /**
   * Shown where the owner's name would be, when the owner has hidden it.
   *
   * It says the CHOICE, not an absence. The owner is named by default and may
   * switch that off, so "not recorded" or an empty cell would both be false —
   * the registry knows who owns the work and has been asked not to say.
   */
  ownerWithheld: "Owner’s name withheld at their request",
  /**
   * Said on a row whose owner has opened the whole record to this estate.
   *
   * Named for what it lets the estate DO rather than for the grant, because the
   * estate's question on this screen is whether the history is there to read.
   */
  recordOpen: "Full record shared with you",

  /**
   * 'Erwin Wurm — 14 works named · 3 with authorship confirmed by this estate'.
   *
   * THE TWO NUMBERS ARE NOT THE SAME KIND OF THING SINCE OND-20, and the old
   * header read as though they were — "14 works · 11 attested by this estate"
   * over a list the estate did not choose. The first count is every work
   * anybody has NAMED this artist on, which is what `ondo.estate_catalogue()`
   * returns and always did. The second is the catalogue proper: the works this
   * estate has confirmed the authorship of, which is also the only thing that
   * issues a number.
   */
  header: (artist: string, works: number, attested: number) =>
    `${artist} — ${String(works)} ${works === 1 ? "work" : "works"} named · ` +
    `${String(attested)} with authorship confirmed by this estate`,

  emptyLead: "No work has been registered naming this artist yet.",
  emptyDetail:
    "Anybody may register a work and name your artist on it, and it appears " +
    "here when they do — that is their attribution, not your agreement to it. " +
    "The catalogue raisonné is what this estate has confirmed: a work is " +
    "numbered when an authorised authority for the artist confirms who made " +
    "it, and by nothing else.",

  /**
   * THE BANNER NO LONGER SAYS NUMBERS FOLLOW REGISTRATION, because they do not.
   *
   * It read "numbers assigned as works are registered", which was true while
   * `works_mint_catalogue_entry` fired on attribution. Since
   * `20261004096000_artist_attribution.sql` the mint fires on a confirming
   * attestation against a live authorship claim instead — "a catalogue number is
   * now issued by a confirmation and by nothing else", so "naming an artist
   * mints nothing, and the forged Fat Car is never row 42". A banner that still
   * promised a number for registering would be the registry telling a forger
   * they were in the catalogue.
   */
  inProgressLead: "Catalogue in progress — a number is issued when this estate confirms who made a work",
  inProgressDetail:
    "Works arrive out of sequence, and most of the list below is unconfirmed: " +
    "somebody named this artist and nobody with authority has answered yet. " +
    "Confirming an authorship claim is what issues the number, and each number " +
    "stays with its work — the numbering is confirmed definitive only when the " +
    "estate declares the catalogue complete.",
  declare: "Declare catalogue complete →",
  /** The second press, because it is one-way; and the way back down. */
  declareConfirm: "Confirm — the numbering becomes definitive",
  declareKeep: "Keep it open",
  declaredLine: (iso: string) =>
    `Catalogue declared complete — definitive numbering since ${new Date(iso).toLocaleDateString(
      "en-GB",
      { day: "numeric", month: "long", year: "numeric" },
    )}.`,

  searchPlaceholder: "Search by CR number, year, title or ONDO id…",
  chipRowLabel: "Filter",
  showing: (shown: number, total: number) =>
    `${String(shown)} of ${String(total)} ${total === 1 ? "work" : "works"}`,
  noMatches: "Nothing in the catalogue matches that.",

  /**
   * Chips. Derived states only; see the model.
   *
   * 'Attested' and 'Not yet attested' are gone and the word was the problem.
   * `CatalogueWork.attested` used to mean "this estate has a live verifying
   * attestation on ANY entry of this work" — so an estate that confirmed an
   * exhibition line in 2019 read as having authenticated the picture, which is
   * what OND-87 forbids in terms: "Confirmation of a loan, sale, insurance or
   * other history fact must not silently confirm artist authorship." It now
   * means the one thing the chip claims, and the chip says which.
   */
  chipLabels: {
    all: "All named",
    attested: "Authorship confirmed by us",
    unattested: "Authorship not confirmed by us",
    edition: "Edition",
  } as const,

  /**
   * The badge pair on a row, and it is THIS estate's own name only.
   *
   * For what any authority has said — which is a different question, and the
   * one that decides whether a work is in the catalogue at all — see
   * `rowStates` below.
   */
  attested: "AUTHORSHIP CONFIRMED BY THIS ESTATE",
  unattested: "AUTHORSHIP NOT CONFIRMED BY THIS ESTATE",

  /**
   * WHAT ANY AUTHORITY HAS SAID, per row — `CatalogueWork.attributionState`.
   *
   * The pair above is caller-relative and this is the work's own. They are
   * different facts: a work another authorised authority confirmed and this
   * estate has not is unattested here and confirmed in the registry, and a
   * catalogue that printed one of those as the other would be wrong in the
   * direction that matters.
   *
   * NOT CONFIRMED IS NOT A VERDICT. Almost every row is in it, because almost
   * nobody has been asked — silence is neither acceptance nor rejection, and
   * nothing in this flow expires.
   */
  rowStates: {
    not_requested: "Authorship not confirmed — no authority has answered",
    awaiting: "Authorship confirmation awaited",
    confirmed: "Authorship confirmed by an authority",
    disputed: "Authorship disputed by an authority",
    /**
     * OND-215. An answer from a party ONDO never appointed over this artist —
     * most often a studio named by email, claimed by whoever controls the
     * address. The estate reading this list is the party that MIGHT be
     * appointed, so the distinction is the most useful thing the row can say:
     * somebody answered, and it was not an authority. Neither row is a
     * catalogue row, which `isConfirmed` decides separately and on purpose.
     */
    confirmed_unappointed: "Authorship confirmed, but not by an appointed authority",
    disputed_unappointed: "Authorship disputed, but not by an appointed authority",
  } as const,
  /**
   * No live authorship claim on the row at all, which is most of the list.
   *
   * Said as what it is — somebody named this artist — rather than as "not
   * confirmed", because there is nothing yet for anybody to have confirmed.
   */
  rowNoClaim: "Named as by this artist — no authorship claim recorded",

  /**
   * A row that kept a number from before a number meant a confirmation.
   *
   * `20261004096000` backfilled nothing and invented no signature: the owner's
   * instruction was to keep earlier catalogue rows and their actual evidence
   * rather than rewrite history, so these keep the number they were given and
   * say what is actually known about them. Flagging them is the whole of how
   * "confirmed works are the catalogue" stays true of a list that still holds
   * numbered rows nobody has confirmed.
   */
  legacyNumber:
    "Numbered before confirmation was required. The number is preserved; the authorship on it is unconfirmed.",

  /**
   * NULL `crSeq` NOW MEANS SOMETHING STRONGER THAN THE DESIGN'S N/A.
   *
   * It used to mean "attributed before the number space existed". It now means
   * no authority has confirmed that this work is by this artist, so no number
   * was issued — registering a work and naming its artist issues none. 'N/A'
   * read as missing data; this reads as the fact it is.
   */
  noNumber: "NOT NUMBERED",
  open: "View",
} as const;

/**
 * The studio's copy.
 *
 * "Never sold" is a badge on the STUDIO's own works and nowhere else, and it is
 * a derived fact rather than a colour of the code: the code has no colour
 * (20260831090000), and the badge reads `neverSold` off the owner's own transfer
 * rows (20260912090000 decision 6). The catalogue stays colourless, as its
 * migration decided.
 *
 * IT SAID "Green · Never sold" AND THE COLOUR WAS NOT ITS TO NAME — OND-99.
 * `StudioWork.neverSold`'s own docblock says it in terms: "NOT a property of the
 * ONDO code, which has no colour." It is one of the two facts the code's ladder
 * needs, and green needs both — no standing transfer AND a signature from an
 * appointed authority. So the badge was announcing a colour from half the
 * question, and on a work registered through some other door and sitting in the
 * studio's collections it announced GREEN while the same work's record showed an
 * amber mark. This screen draws no mark beside the badge, so the contradiction
 * had nowhere to show and a reader had to click through to meet it.
 *
 * A SCREEN NAMES THE COLOUR ONLY WHERE IT COMPUTED IT. The gallery's inventory
 * row does name it, and may: `ConsignedWork` carries both facts and the row
 * passes them to `stateOf`. This row holds one fact, so it says the one fact,
 * which is true of every work it is drawn on. Nothing was lost but a claim that
 * was not being checked — and OND-63's green is intact where it is actually
 * read, which is the mark on the work's own record and on a gallery's inventory.
 */
export const STUDIO_COPY = {
  loading: "Opening the studio book…",
  errorTitle: "Can’t load the production records",

  /** The two tabs the design draws. */
  recordsTab: "Production records",
  importTab: "Data import",
  importLead: "Data import is not built yet.",
  importDetail:
    "The design names the tab and nothing more. What it waits on is a decision " +
    "about which spreadsheet shapes the studio actually keeps — until then the " +
    "records tab is the one door, and it registers one work at a time.",

  register: "+ Register new work",

  summary: (n: number) => `${String(n)} ${n === 1 ? "work" : "works"} · editions and studio originals`,

  emptyLead: "The studio holds no works of its own yet.",
  /**
   * NUMBERED AGAIN, AND EARNED THIS TIME — OND-63.
   *
   * This sentence has been three things in three days and the history is worth
   * keeping, because each version was true of the code it was written for.
   * "Joins the catalogue under the artist's name" was true while
   * `works_mint_catalogue_entry` fired on attribution — anybody naming a linked
   * artist got a number. OND-20 moved the mint onto a confirming attestation,
   * so it became "not yet numbered — that is a separate act even for the
   * studio's own", which was the screen OND-63 was filed about: the appointed
   * authority told its own brand-new work awaited somebody's determination.
   *
   * `20261004099000` records the studio's own confirmation at registration, on
   * the owner's answer that "registering a work counts as signing for it". So
   * the number is back AND the condition is unchanged: it is issued by a
   * confirmed authorship claim and by nothing else. What changed is that the
   * studio makes and confirms that claim in the same act, which it is the one
   * party entitled to do.
   *
   * IT STILL SAYS WHAT THE CONDITION IS rather than only the outcome, because
   * the outcome is not true of a work registered anywhere else on ONDO — and a
   * studio director who read "registering numbers it" here would expect the
   * same of the collector wizard, where it would be false.
   */
  emptyDetail:
    "Register a work here and it is the studio’s own: it takes an ONDO code, " +
    "is listed under the artist’s name, and keeps its private production " +
    "record beside it. It is numbered as you register it — a catalogue number " +
    "is issued when an authorised authority confirms who made a work, and " +
    "registering your own work here is you confirming it. Works registered " +
    "into your collections elsewhere appear here too.",

  /** The derived badge — see the header for why it names no colour. */
  neverSold: "Never sold",

  /** The field labels, as the design writes them. */
  cost: "Cost of production",
  insured: "Insured value",
  retail: "Retail value (proj.)",
  series: "Whole-series value",
  framing: "Framing",
  location: "Location",
  currency: "Currency",
  produced: "Produced (of edition)",

  editionLine: (produced: number, size: number) =>
    `Edition ${String(produced)}/${String(size)} produced`,

  lifecycle: {
    conceived: "Conceived",
    in_production: "In production",
    produced: "Produced",
    with_gallery: "With gallery",
  } as const,

  withGallery: (name: string) => `With gallery — ${name}`,
  edit: "Edit record",
  save: "Save record",
  cancel: "Cancel",
  none: "—",

  badFigure: "A figure must be a positive amount, to two decimal places at most.",
  needsCurrency: "Name the currency the figures are in — three letters.",
  badCount: "Produced is a whole number.",

  footer:
    "These fields are private to the studio and never shown on the " +
    "collector-facing record. The lifecycle reads from your own tally against " +
    "the edition — Conceived → In production → Produced → With gallery — and " +
    "nothing stores it as a status.",
} as const;

/**
 * The register dialog. Four steps, the design's, with two sentences changed
 * for the two things the registry cannot say: the code is shown AFTER
 * registering rather than on Confirm, because the insert mints it; and the
 * Status control saves a produced count, because nothing stores a status.
 */
export const STUDIO_REGISTER_COPY = {
  title: "Register a new work",
  subtitle: "Studio original · registered as the artist’s own, never sold",
  close: "Close",

  steps: {
    details: "Details",
    images: "Images",
    provenance: "Provenance",
    confirm: "Confirm",
  } as const,

  /** Details. */
  workTitle: "Title *",
  titlePlaceholder: "e.g. Standing Figure (green)",
  year: "Year",
  yearPlaceholder: "e.g. 2026",
  medium: "Medium",
  mediumPlaceholder: "e.g. Bronze",
  dimensions: "Dimensions",
  dimensionsPlaceholder: "e.g. 180 × 60 cm",
  edition: "Edition size",
  status: "Status",
  statuses: {
    conceived: "Conceived",
    in_production: "In production",
    produced: "Produced",
  } as const,
  producedSoFar: "Produced so far",
  location: "Location",
  locationPlaceholder: "e.g. Studio — Vienna",
  cost: "Cost of production",
  insured: "Insured value",
  retail: "Retail value (projected)",
  series: "Whole-series value",
  currency: "Currency",

  /** Images. */
  imagesLead: "Add studio photography",
  imagesDetail: "Front, frame, back, side, details — JPEG, PNG, TIFF, HEIC or WebP",
  addImage: "Add image",
  removeImage: "Remove",
  imageKind: (name: string) => `What ${name} shows`,
  images: "Images",
  imageCount: (n: number) => (n === 0 ? "None" : `${String(n)} to upload`),

  /** Provenance. */
  originLegend: "Provenance — origin",
  origin: "Created by the artist — retained in studio",
  notes: "Notes",
  notesPlaceholder: "Casting notes, foundry, references…",
  /**
   * THE REGISTRATION IS SIGNED FOR AGAIN, AND BY THE ONE PARTY THAT MAY — OND-63.
   *
   * OND-20 removed the claim that it was, correctly: the registry had never
   * signed anything, and "a confirmation is an explicit answer to an explicit
   * ask". `20261004099000` is the answer to the question that left open —
   * whether an authorised studio registering its own work is an exception —
   * and the owner answered it in one sentence: "registering a work counts as
   * signing for it. The studio is the appointed authority and it is standing
   * behind the opening line."
   *
   * SO THIS SAYS THE SECOND SENTENCE OUT LOUD rather than only its effect. A
   * director is putting the studio's name on the public record by pressing
   * Register, which is a thing somebody is entitled to be told before they
   * press it — and it is bounded by the studio's live appointment over the
   * artist, which this door has checked since 20260912090000.
   *
   * It still names no other body, for the reason the register wizard's own copy
   * gives: the only authority this sentence may name is the one the caller is
   * already acting as.
   */
  provenanceNote:
    "As a work created and first held by the artist, this is registered never " +
    "sold. Ownership and provenance start from the studio: the sentence above " +
    "and your notes become the first line of its history on the work record. " +
    "Registering it also records the studio’s confirmation that this artist " +
    "made it — you are the appointed authority, so registering your own work " +
    "is signing for it, and the work is numbered by that.",

  /** Confirm. */
  editionSummary: (produced: number, size: number) =>
    `${String(produced)} of ${String(size)} produced`,
  codePending: "ONDO code issued on registering",
  /**
   * On the registered panel — the same derived badge the card shows, and it
   * names no colour for the same reason: see `STUDIO_COPY`'s header.
   *
   * This panel is the one place a colour claim would have been TRUE, because the
   * door it follows is the one that signs for its own work — so after OND-63 the
   * mark on this work is green on every screen that reads the ladder. It still
   * does not say so, because this panel reads no more than the card does and a
   * sentence that is true by inference from another file is the thing that goes
   * quietly wrong. The work's own record says green, having asked.
   */
  neverSoldBadge: "Never sold",
  /**
   * The last thing a director reads before pressing Register, so it is the one
   * place the whole of what happens has to be true — OND-63.
   *
   * It said "issues no catalogue number — a number comes from a confirmed
   * authorship claim, which is a separate act", which was OND-20's code exactly.
   * `20261004099000` makes the claim and its confirmation part of this act, so
   * the number comes with it. The CONDITION is unchanged and is still stated,
   * because it is what makes the number mean anything: a number is issued by a
   * confirmed authorship claim, and here the confirming authority is you.
   */
  confirmNote:
    "Registering issues the ONDO code and adds the work to production records " +
    "with the status you chose. It records the artist as an attribution and " +
    "your own confirmation of it — a number comes from a confirmed authorship " +
    "claim, and as the appointed authority for this artist you are confirming " +
    "it by registering the work.",
  none: "—",

  /** Buttons. */
  cancel: "Cancel",
  back: "← Back",
  next: "Continue →",
  submit: "Register work ✓",
  submitting: "Registering…",

  /** Registered. */
  registeredLead: (title: string) => `“${title}” is registered.`,
  /**
   * "In the catalogue under the artist's name" is the one claim this panel used
   * to make that OND-20 took away. It is listed, not numbered.
   */
  registeredDetail:
    "It is in production records with its private book, and listed under the " +
    "artist’s name. It carries no catalogue number yet: a number is issued " +
    "when an authorised authority confirms the authorship.",
  openRecord: "Open the work record",
  registerAnother: "Register another",
  done: "Done",
  /** An image that failed after the work exists — the work stays. */
  imagesFailed: (n: number) =>
    `The work is registered, but ${String(n)} ${n === 1 ? "photograph" : "photographs"} did not upload. Add them from the work record.`,

  /** Refusals — client-side mirrors; the server decides. */
  failed: "Could not register the work.",
  needsTitle: "Give the work a title.",
  badEdition: "Edition size is a whole number, 1 or more.",
  needsProducedSoFar: "Say how many of the edition are produced so far — at least 1.",
  producedExceedsEdition: (size: number) =>
    `Fewer than ${String(size)} — at ${String(size)} the edition is produced, not in production.`,
  badFigure: "A figure must be a positive amount, to two decimal places at most.",
  needsCurrency: "Name the currency the figures are in — three letters.",
} as const;

/**
 * Loans & Consignments. Both footnotes are the design's, kept because both
 * are TRUE of the schema: a loan writes no transfer, and a consignment leaves
 * title with the consignor until `ondo.effect_sale()` says otherwise.
 */
export const LOANS_COPY = {
  loading: "Loading loans and consignments…",
  errorTitle: "Can’t load loans and consignments",

  loanTab: "Loan — to any counterparty",
  consignTab: "Consign — to a gallery or agent",

  /** Form labels. */
  work: "Work",
  pickWork: "Choose a work you hold…",
  venue: "Venue / borrower",
  venuePlaceholder: "e.g. Tate Modern, London",
  borrower: "ONDO id (optional)",
  borrowerPlaceholder: "ONDO-0000 — leave blank for a venue not on ONDO",
  // CORRECTED BY OND-9, and the old sentence is worth knowing because it was
  // true when written. It said acceptance "opens a revocable view of the work
  // for them" — which was the whole of their access, and the defect: a museum
  // was asked to take custody of an object it could not look at. A live
  // PROPOSAL now opens a read of its own (ondo.loan_proposed_work_ids), and
  // what acceptance adds is the revocable GRANT that outlasts the question.
  borrowerNote:
    "Named, the borrower is ASKED and must accept before the loan stands. " +
    "While the question is open they can see the work; accepting turns that " +
    "into a revocable view that lasts the loan. Blank, the loan is your own " +
    "record and stands at once.",
  loanStart: "Loan start",
  loanEnd: "Loan end",
  purpose: "Exhibition / purpose",
  purposePlaceholder: "e.g. Erwin Wurm, Tate Modern",
  initiateLoan: "Initiate loan →",
  loanFootnote:
    "A loan keeps ownership with you. It is recorded on the ONDO ledger; " +
    "insurance and carriage stay between you and the borrower.",

  gallery: "Gallery / agent — by ONDO id",
  galleryPlaceholder: "ONDO-0000",
  galleryNote:
    "The id is resolved on the server with one answer covering every way it " +
    "can be wrong. Nothing here looks a name up.",
  asking: "Asking price (optional)",
  currency: "Currency",
  askingPlaceholder: "e.g. 920,000",
  note: "Note / terms (optional)",
  notePlaceholder: "e.g. 15% commission as agreed",
  consignWork: "Consign work →",
  consignFootnote:
    "A consignment authorises the gallery to represent and sell the work; " +
    "you retain title until it sells or is returned.",

  /** Refusals, client-side mirrors of server rules. */
  needsWork: "Choose which work.",
  needsVenue: "Say where it is going.",
  needsOndoId: "A gallery or agent is named by ONDO id — ONDO-0000.",
  badAsking: "The asking price must be a positive amount.",
  datesBackwards: "The loan cannot end before it starts.",

  /** Confirmations, read off what the server actually did. */
  loanProposed: (title: string) =>
    `“${title}” proposed. The borrower sees the loan and must accept it before it stands.`,
  loanRecorded: (title: string) =>
    `“${title}” recorded as on loan. The venue is not on ONDO, so there was nobody to ask.`,
  consignOffered: (title: string) =>
    `“${title}” offered. It appears in the gallery’s inventory when they accept.`,

  /** The rail. */
  railTitle: "Active loans & consignments",
  railEmpty: "Nothing is out. A loan or a consignment you initiate appears here.",
  onLoanTo: (venue: string) => `On loan to ${venue}`,
  consignedTo: (name: string) => `Consigned to ${name}`,
  offerPending: "Offered — not yet answered",
  offerHeld: "Held by the gallery",
  from: "from",
  until: "until",
  cancel: "Cancel loan",

  // THE LENDER'S SEND BUTTON, which `ondo.mark_loan_out()` has waited for since
  // 20260804090200. Without it the borrower's receipt and return are
  // unreachable code: 'out' is the only state either can be made from.
  dispatch: "Send work out",
  dispatchHint: "Records that the work has left. The borrower confirms its arrival themselves.",

  // 'declined' JOINED ondo.loan_status IN 20261003094000 AND WAS MISSING HERE.
  //
  // The cast below is an assertion rather than a check, so a status with no
  // line in this map renders `undefined` in the rail rather than failing a
  // build — which is exactly what a refused loan did between the migration and
  // this line. Worth knowing before adding a seventh status.
  //
  // "Declined" and not "Cancelled": the borrower refused, and the lender's own
  // word for their own act is one row down.
  loanStates: {
    proposed: "Proposed",
    agreed: "Agreed",
    declined: "Declined",
    out: "Out",
    returned: "Returned",
    cancelled: "Cancelled",
  } as Record<LoanStatus, string>,
} as const;

/**
 * The studio's authentication fees on the Transaction Centre — OND-70.
 *
 * READ-ONLY HERE. The fee is `ondo.attribution_fees` (OND-60), raised by the
 * database when a studio substantively answers a confirmation request, and the
 * two acts on it — recording a payment, appending a correction — stay on the
 * Verifications tab beside the case they belong to. This centre shows the
 * ledger the way the gallery's shows registration fees: what is recorded, per
 * currency, and never a figure ONDO collected.
 *
 * "Recorded as paid", never "paid": ONDO takes no money and observes no payment.
 */
export const ATTRIBUTION_FEES_COPY = {
  heading: "Authentication fees",
  lead: "What answering a confirmation request has been recorded as owing you, and what has been recorded as paid.",
  loading: "Reading the fee ledger…",
  errorTitle: "Can’t load the authentication fees",
  empty: "No authentication fee has been recorded yet.",
  emptyDetail:
    "A fee is recorded the moment the studio substantively answers a request to confirm an attribution — confirmed or rejected, both are charged. Declining to answer records nothing.",
  ledger: (n: number) => `${n} ${n === 1 ? "fee" : "fees"}`,
  totalsLabel: "Fee totals by currency",
  totals: {
    owed: (currency: string) => `Owed to you · ${currency}`,
    paid: (currency: string) => `Recorded as paid · ${currency}`,
    rows: (n: number) => `${n} ${n === 1 ? "fee" : "fees"}`,
    voided: (n: number) => `${n} voided`,
    refunds: (n: number) => `${n} refund${n === 1 ? "" : "s"} owed`,
  },
  askedBy: "Asked by",
  answered: { confirmed: "Confirmed", rejected: "Rejected" },
  where: "Payments and corrections are recorded on the Verifications tab, beside the case.",
} as const;
